import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useNotification } from "~hooks/useNotification";
import type { DefaultOperationSuccessResponse } from "~types/response";

const useDeleteCompetition = () => {
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const request = useApi();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();

  return useMutation<DefaultOperationSuccessResponse, Error, string>({
    mutationFn: async (competitionId: string) => {
      const url = `${sport}/o/${organizationId}/competitions/${competitionId}`;
      return await request(url, {
        method: "DELETE",
      });
    },
    onSuccess: async (_, competitionId) => {
      queryClient.setQueryData(["permission-scope-current-user", sport, organizationId], (oldScope) => {
        if (!oldScope) {
          return;
        }

        const updatedScope = JSON.parse(JSON.stringify(oldScope));
        const competitionToDelete = updatedScope.resources?.competitions?.[competitionId];

        if (competitionToDelete) {
          const leagueToUpdate = updatedScope.scope?.leagueTree?.find(({ leagueId }) => leagueId === competitionToDelete.leagueId);
          leagueToUpdate.competitions = leagueToUpdate.competitions.filter((competition) => competition.competitionId !== competitionId);
          delete updatedScope.resources.competitions[competitionId];
        }

        return updatedScope;
      });

      notification.success({
        message: formatMessage({ id: "competition.delete.success" }),
        placement: "bottomRight",
      });

      await invalidateCache();
    },
  });
};

export default useDeleteCompetition;
